import type { ScoreSystemData } from '../hooks/usePdfStateQueue';

export interface FingeringGroupRef {
  imageIndex: number;
  groupIndex: number;
}

/**
 * Walks outward from a starting `(imageIndex, groupIndex)` and collects
 * every adjacent fingering group (in the same direction) that has at
 * least one digit whose main and alt actually differ — i.e. groups
 * that are meaningful to swap together. Stops at the first
 * matching-direction group whose digits are all main===alt (nothing
 * useful to swap) and at system boundaries when there's no follow-on
 * system.
 *
 * The returned array is in document order (forward and backward
 * collection are merged via `unshift` / `push` accordingly).
 *
 * Pure data traversal — no React, no DOM. The page passes
 * `currentFingeringRef.current` directly so the result reflects the
 * absolute latest edit state (the ref always lags the queue by zero
 * cycles).
 */
export const collectSwapGroups = (
  fingeringData: ScoreSystemData[] | null,
  startImageIndex: number,
  startGroupIndex: number
): FingeringGroupRef[] => {
  if (!fingeringData) return [];

  const collected: FingeringGroupRef[] = [];

  const startSystem = fingeringData[startImageIndex];
  if (!startSystem) return collected;

  const startGroup = startSystem.fingeringGroupList[startGroupIndex];
  if (!startGroup) return collected;

  const isUpward = startGroup.coordinate.upward;

  // True when this group (same direction) has at least one digit whose
  // main and alt actually differ. Comparing *sets* of finger numbers was
  // wrong: main=[1,2] / alt=[2,1] has identical sets but is a real swap
  // — short-click group swap used to no-op on exactly those cases.
  const shouldSwapGroup = (imageIndex: number, groupIndex: number): boolean => {
    const system = fingeringData[imageIndex];
    if (!system) return false;
    const group = system.fingeringGroupList[groupIndex];
    if (!group) return false;
    if (group.coordinate.upward !== isUpward) return false;

    for (const fingeringNum of group.fingeringNumList) {
      if (
        fingeringNum.altFingering != null &&
        fingeringNum.altFingering !== 0 &&
        fingeringNum.mainFingering !== fingeringNum.altFingering
      ) {
        return true;
      }
    }
    return false;
  };

  // Walks forward from the starting position, hopping across
  // systems when it runs off the end. Skips groups with the
  // wrong direction; stops at the first matching-direction
  // group with nothing useful to swap (`shouldSwapGroup`
  // returns false).
  const collectForward = (imageIndex: number, groupIndex: number) => {
    const system = fingeringData[imageIndex];
    if (!system) return;

    if (groupIndex >= system.fingeringGroupList.length) {
      if (imageIndex + 1 < fingeringData.length) {
        collectForward(imageIndex + 1, 0);
      }
      return;
    }

    const currentGroup = system.fingeringGroupList[groupIndex];
    if (!currentGroup || currentGroup.coordinate.upward !== isUpward) {
      collectForward(imageIndex, groupIndex + 1);
      return;
    }

    if (shouldSwapGroup(imageIndex, groupIndex)) {
      collected.push({ imageIndex, groupIndex });
      collectForward(imageIndex, groupIndex + 1);
    }
  };

  // Mirror of `collectForward`, but walks backward and `unshift`s
  // so the final array stays in document order.
  const collectBackward = (imageIndex: number, groupIndex: number) => {
    const system = fingeringData[imageIndex];
    if (!system) return;

    if (groupIndex < 0) {
      if (imageIndex - 1 >= 0) {
        const prevSystem = fingeringData[imageIndex - 1];
        if (prevSystem && prevSystem.fingeringGroupList.length > 0) {
          collectBackward(
            imageIndex - 1,
            prevSystem.fingeringGroupList.length - 1
          );
        }
      }
      return;
    }

    const currentGroup = system.fingeringGroupList[groupIndex];
    if (!currentGroup || currentGroup.coordinate.upward !== isUpward) {
      collectBackward(imageIndex, groupIndex - 1);
      return;
    }

    if (shouldSwapGroup(imageIndex, groupIndex)) {
      collected.unshift({ imageIndex, groupIndex });
      collectBackward(imageIndex, groupIndex - 1);
    }
  };

  // Collect backward from the group before the starting group
  collectBackward(startImageIndex, startGroupIndex - 1);

  // Add the starting group
  if (shouldSwapGroup(startImageIndex, startGroupIndex)) {
    collected.push({
      imageIndex: startImageIndex,
      groupIndex: startGroupIndex,
    });
  }

  // Collect forward from the group after the starting group
  collectForward(startImageIndex, startGroupIndex + 1);

  return collected;
};
